import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Bearer token support — used as fallback when cookies aren't available (e.g. Teams)
export function setAuthToken(token) {
  client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export function clearAuthToken() {
  delete client.defaults.headers.common['Authorization'];
}

// 401 interceptor: attempt silent refresh before failing
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed() {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401, not on refresh endpoint itself, and only once per request
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/sso')
    ) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const res = await client.post('/auth/refresh');
          // If server returned a token in body (for Bearer fallback), update header
          if (res.data?.token) {
            setAuthToken(res.data.token);
          }
          isRefreshing = false;
          onRefreshed();
          return client(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = [];
          // Refresh failed — let the 401 propagate so AuthContext handles logout
          return Promise.reject(error);
        }
      }

      // Another request is already refreshing — queue this one
      return new Promise((resolve) => {
        addRefreshSubscriber(() => {
          resolve(client(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

export async function apiGet(path, params) {
  const res = await client.get(path, { params });
  return res.data;
}

export async function apiPost(path, data, config) {
  const res = await client.post(path, data, config);
  return res.data;
}

export async function apiPut(path, data, config) {
  const res = await client.put(path, data, config);
  return res.data;
}

export async function apiPatch(path, data) {
  const res = await client.patch(path, data);
  return res.data;
}

export async function apiDelete(path) {
  const res = await client.delete(path);
  return res.data;
}

export function getUploadUrl(key) {
  return `${API}/uploads/${key}`;
}

export { API, BACKEND_URL };
